<template>
  <div class="docs-pane-rendered">
    <div
      v-if="!body"
      class="docs-pane-rendered__empty text-muted text-center"
    >
      (aucune version publiée — cet article est nouveau)
    </div>
    <!-- eslint-disable-next-line vue/no-v-html — markdown-it est configure
         avec html:false, donc tout HTML inline dans le MD source est echappe -->
    <div
      v-else
      ref="rendered"
      class="docs-pane-rendered__body markdown-body"
      v-html="renderedHtml"
    />
  </div>
</template>

<script>
// Rendu Markdown de la "Version publiee" cote admin docs. Charge les
// images locales (_images/<name>) via la route admin authentifiee
// `/admin/docs/images/static/:name` en mode blob -> objectURL (le
// <img src> natif ne porte pas l'Authorization, idem pattern que
// ImageLibraryModal).

import axios from '@axios'
import MarkdownIt from 'markdown-it'

const DOCS_API_BASE = 'https://api2.buxida.com'

// Placeholder transparent 1x1 le temps que le blob authentifie arrive.
const IMG_PLACEHOLDER = 'data:image/svg+xml;base64,'
  + 'PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxIiBoZWlnaHQ9IjEiLz4='

const LOCAL_IMG_RE = /^_images\/([a-z0-9._-]+)$/i

function stripFrontMatter(content) {
  if (!content) return ''
  // Strip BOM si present (caractere U+FEFF en tete de fichier MD).
  const s = content.replace(/^\uFEFF/, '')
  if (!s.startsWith('---\n') && !s.startsWith('---\r\n')) return s
  const m = s.match(/^---\r?\n[\s\S]*?\r?\n---\r?\n/)
  if (!m) return s
  return s.slice(m[0].length)
}

export default {
  name: 'PublishedPreview',
  props: {
    content: { type: String, default: '' },
  },
  data() {
    return {
      blobs: {},
    }
  },
  computed: {
    body() {
      return stripFrontMatter(this.content)
    },
    renderedHtml() {
      if (!this.body) return ''
      return this.md.render(this.body)
    },
  },
  watch: {
    renderedHtml() {
      this.$nextTick(() => this.loadImages())
    },
  },
  created() {
    this.md = new MarkdownIt({
      html: false,
      linkify: true,
      breaks: false,
      typographer: false,
    })
    const defaultImage = this.md.renderer.rules.image
      || ((tokens, idx, opts, env, self) => self.renderToken(tokens, idx, opts))
    this.md.renderer.rules.image = (tokens, idx, opts, env, self) => {
      const token = tokens[idx]
      const src = token.attrGet('src') || ''
      const m = LOCAL_IMG_RE.exec(src)
      if (m) {
        token.attrSet('src', IMG_PLACEHOLDER)
        token.attrSet('data-buxida-image', m[1])
        token.attrJoin('class', 'docs-img-pending')
      }
      return defaultImage(tokens, idx, opts, env, self)
    }
  },
  mounted() {
    this.$nextTick(() => this.loadImages())
  },
  beforeDestroy() {
    this.revokeAll()
  },
  methods: {
    revokeAll() {
      Object.keys(this.blobs).forEach(name => {
        try { URL.revokeObjectURL(this.blobs[name]) } catch (_) { /* noop */ }
      })
      this.blobs = {}
    },
    async loadImages() {
      const root = this.$refs.rendered
      if (!root) return
      const imgs = root.querySelectorAll('img[data-buxida-image]')
      const names = new Set()
      imgs.forEach(img => names.add(img.getAttribute('data-buxida-image')))
      await Promise.all([...names].map(n => this.loadOne(n)))
      // Patch les <img> avec leur blob une fois tous re&ccedil;us.
      imgs.forEach(img => {
        const name = img.getAttribute('data-buxida-image')
        const url = this.blobs[name]
        if (url) {
          img.setAttribute('src', url)
          img.classList.remove('docs-img-pending')
        }
      })
    },
    async loadOne(name) {
      if (this.blobs[name]) return
      try {
        const response = await axios.get(
          `${DOCS_API_BASE}/admin/docs/images/static/${encodeURIComponent(name)}`,
          { responseType: 'blob' },
        )
        const url = URL.createObjectURL(response.data)
        this.$set(this.blobs, name, url)
      } catch (_) {
        // Image manquante ou non-autorisee : le placeholder reste.
      }
    },
  },
}
</script>

<style scoped>
.docs-pane-rendered {
  background: #fff;
  border: 1px solid #e3e3e3;
  border-radius: 4px;
  padding: 16px;
  max-height: 60vh;
  overflow-y: auto;
  font-size: 14px;
  line-height: 1.55;
}

.docs-pane-rendered__empty {
  padding: 32px 12px;
}

.docs-pane-rendered__body ::v-deep h1,
.docs-pane-rendered__body ::v-deep h2,
.docs-pane-rendered__body ::v-deep h3,
.docs-pane-rendered__body ::v-deep h4 {
  margin-top: 1.2em;
  margin-bottom: 0.5em;
  line-height: 1.25;
}

.docs-pane-rendered__body ::v-deep h1 { font-size: 1.5em; }
.docs-pane-rendered__body ::v-deep h2 { font-size: 1.3em; }
.docs-pane-rendered__body ::v-deep h3 { font-size: 1.1em; }
.docs-pane-rendered__body ::v-deep h4 { font-size: 1em; }

.docs-pane-rendered__body ::v-deep p {
  margin: 0.5em 0;
}

.docs-pane-rendered__body ::v-deep ul,
.docs-pane-rendered__body ::v-deep ol {
  margin: 0.5em 0;
  padding-left: 1.5em;
}

.docs-pane-rendered__body ::v-deep img {
  max-width: 100%;
  height: auto;
  border: 1px solid #eee;
  border-radius: 4px;
  margin: 6px 0;
  display: block;
}

.docs-pane-rendered__body ::v-deep img.docs-img-pending {
  background: #f8f8f8;
  min-height: 60px;
  min-width: 60px;
}

.docs-pane-rendered__body ::v-deep pre {
  background: #f6f8fa;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
}

.docs-pane-rendered__body ::v-deep code {
  background: #f6f8fa;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.9em;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
}

.docs-pane-rendered__body ::v-deep pre code {
  background: transparent;
  padding: 0;
}

.docs-pane-rendered__body ::v-deep blockquote {
  border-left: 3px solid #ccc;
  padding-left: 12px;
  margin: 0.5em 0;
  color: #555;
}

.docs-pane-rendered__body ::v-deep table {
  border-collapse: collapse;
  margin: 0.5em 0;
  font-size: 13px;
}

.docs-pane-rendered__body ::v-deep table th,
.docs-pane-rendered__body ::v-deep table td {
  border: 1px solid #ddd;
  padding: 4px 8px;
}

.docs-pane-rendered__body ::v-deep table th {
  background: #f6f8fa;
  font-weight: 600;
}

.docs-pane-rendered__body ::v-deep a {
  color: #1976d2;
  text-decoration: underline;
}

.docs-pane-rendered__body ::v-deep hr {
  border: 0;
  border-top: 1px solid #eee;
  margin: 1em 0;
}
</style>
